import React, {useEffect} from 'react'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGlobaleContext} from '../../contexts/globale'

const GlobalEScript = () => {
    const appOrigin = getAppOrigin()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {site} = useMultiSite()
    const {countryISO: countryISOContext, currencyCode: currencyCodeContext} = useGlobaleContext()
    const {globalE} = commerceAPI
    const {config} = globalE || {}

    var geEnabled = false
    var geClientJsMerchantId = null
    var geApiVersion = null
    var geClientDomain = null

    try {
        const jsonConfig = JSON.parse(config)
        geEnabled = jsonConfig.enabled
        geClientJsMerchantId = jsonConfig.clientJSMerchantID
        geApiVersion = jsonConfig.apiVersion
        geClientDomain = jsonConfig.clientDomain
    } catch (e) {
        console.log(e.message)
    }

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    useEffect(() => {
        if (!geClientJsMerchantId || !geApiVersion || !site?.id || !geClientDomain || !geEnabled) {
            return
        }

        var globaleData = cookie.get('GlobalE_Data')
        var countryCode =
            countryISOContext ||
            (globaleData && JSON.parse(globaleData)?.countryISO
                ? JSON.parse(globaleData).countryISO
                : cookie.get('countryCode'))
        var currencyCode =
            currencyCodeContext ||
            (globaleData && JSON.parse(globaleData)?.currencyCode
                ? JSON.parse(globaleData).currencyCode
                : cookie.get('currencyCode'))
        var cultureCode =
            globaleData && JSON.parse(globaleData)?.cultureCode
                ? JSON.parse(globaleData).cultureCode
                : cookie.get('cultureCode')
        if (!currencyCode || !countryCode) {
            return
        }
        const domain = new URL(appOrigin).hostname
        const initScriptLoaderData = () => {
            try {
                window.geScriptLoaderData = {
                    locale: 'en_US', // hardcode
                    clientJsUrl: `${geClientDomain}/merchant/clientsdk/${geClientJsMerchantId}`,
                    apiVersion: geApiVersion,
                    clientJsMerchantId: geClientJsMerchantId,
                    clientSettings: localStorage.getItem('clientSettings'),
                    clientJsDomain: geClientDomain,
                    cookieDomain: domain,
                    globaleOperatedCountry: countryCode !== 'US',
                    geoLocationCountry: {
                        countryCode: countryCode,
                        isCountryExists: true
                    },
                    siteId: site?.id,
                    country: countryCode,
                    currency: currencyCode,
                    culture: cultureCode,
                    allowedCurrencies: {
                        default: []
                    }
                }
            } catch {
                window.geScriptLoaderData = null
            }
        }

        const initClientSDK = () => {
            if (!window.geScriptLoaderData) return Promise.reject()
            return new Promise((resolve, reject) => {
                if (!window.gleLoaded) {
                    window.globaleObject = 'gle'
                    window.gle =
                        window.gle ||
                        function () {
                            ;(window.gle.q = window.gle.q || []).push(arguments)
                        }
                    window.gle.m = window.geScriptLoaderData.clientJsMerchantId
                    window.gle.v = window.geScriptLoaderData.apiVersion

                    const s = document.createElement('script')
                    s.src = `${window.geScriptLoaderData.clientJsUrl}?v=${window.geScriptLoaderData.apiVersion}`
                    s.async = true
                    s.onerror = reject
                    s.onload = () => resolve()
                    document.head.appendChild(s)
                    window.gleLoaded = true
                } else if (document.querySelector('#gle_iframe')) {
                    resolve()
                }
            })
        }

        const onShippingSwitcherClosedHandler = (event) => {
            try {
                if (
                    event.button === 'save' &&
                    (event.country !== window.geScriptLoaderData.country ||
                        event.currency !== window.geScriptLoaderData.currency)
                ) {
                    if (!event.isRedirect) {
                        window.GlobalE.UpdateCustomerInfo(event.country, event.currency, false)
                        window.location.reload()
                    }
                }
            } catch (e) {
                console.error(e)
            }
            return false
        }

        /**
         * Init 'KeepAlive' feature
         */
        const initKeepAlive = () => {
            var geIframe = document.querySelector('#gle_iframe')
            try {
                window.globaleKeepAlive = setInterval(function () {
                    Helpers.makeJsonAjaxCall(geIframe.dataset.keepaliveUrl, 'GET') // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        .then(function (response) {
                            // handle response
                        })
                        .catch(function (e) {
                            console.warn(e)
                        })
                }, 60000) // every 1 minute
            } catch (e) {
                console.warn(e)
            }
        }

        /**
         * Customer registration post message handler
         * @param {Object} event - Event object
         */
        const registrationPostMessageHandler = (event) => {
            try {
                // check request is from legitimate source and message is expected or not
                if (
                    !window.GlobalE ||
                    !window.GlobalE.CheckoutManager ||
                    !window.GlobalE.CheckoutManager.IsGlobaleMessage(event.origin)
                ) {
                    return
                }
                var eventMessage =
                    typeof event.data === 'string' ? JSON.parse(event.data) : event.data
                var geIframe = document.querySelector('#gle_iframe')
                if ('Key' in eventMessage && eventMessage.Key === 'CustomerRegistration') {
                    Helpers.makeJsonAjaxCall(
                        geIframe.dataset.registrationUrl,
                        'POST',
                        JSON.stringify(eventMessage.Data)
                    )
                        .then(function (response) {
                            if (response) {
                                response = JSON.parse(response)
                                window.GlobalE.CheckoutManager.UpdateGlobalE(
                                    'CustomerRegistration',
                                    response
                                )
                            }
                        })
                        .catch(function (e) {
                            console.warn(e)
                        })
                }
            } catch (e) {
                console.warn(e)
            }
        }

        var Helpers = {
            makeJsonAjaxCall: function (url, type, data) {
                var promise = new Promise(function (resolve, reject) {
                    var xhr = new XMLHttpRequest()
                    xhr.open(type, url, true)
                    xhr.setRequestHeader('Cache-Control', 'no-cache')
                    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
                    xhr.send(data || null)
                    xhr.onreadystatechange = function () {
                        if (xhr.readyState === 4) {
                            if (xhr.status === 200) {
                                resolve(xhr.responseText)
                            } else {
                                reject(xhr.status)
                            }
                        }
                    }
                })
                return promise
            },
            removeParametersFromQueryString: function (queryString, parameters) {
                var qsParams =
                    queryString && typeof queryString === 'string' && queryString.length > 0
                        ? queryString.split('&')
                        : []
                var removedParams =
                    parameters && Array.isArray(parameters) && parameters.length > 0
                        ? parameters
                        : []
                removedParams.forEach(function (param) {
                    var prefix = encodeURIComponent(param) + '='
                    for (var i = qsParams.length; i-- > 0; ) {
                        if (qsParams[i].lastIndexOf(prefix, 0) !== -1) {
                            qsParams.splice(i, 1)
                        }
                    }
                })
                return qsParams.join('&')
            }
        }

        /**
         * Handler function of 'GlobalE.OnCheckoutStepLoadedHandler' event
         * @param {Object} data - Event data
         */
        const onCheckoutStepLoadedHandler = (data) => {
            try {
                if (data.StepId === data.Steps.CONFIRMATION) {
                    // stop sending keepAlive ajax call
                    if ('globaleKeepAlive' in window && window.globaleKeepAlive) {
                        clearInterval(window.globaleKeepAlive)
                    }
                }

                /**
                 * If you need to implement the frontend analytics please put it here
                 */

                /**
                 * Only for Backend Analytics, like standard SFCC Merchant Tools / Analytics / Purchase Reports.
                 * Should be handled in Globale-Analytics controller
                 */
                var geIframe = document.querySelector('#gle_iframe')
                Helpers.makeJsonAjaxCall(
                    geIframe.dataset.analyticsUrl,
                    'POST',
                    JSON.stringify(data)
                )
                    .then(function (response) {
                        // handle response for confirmation
                        if (response.data.StepId === response.data.Steps.CONFIRMATION) {
                            //send GTM for confirmation
                        }
                    })
                    .catch(function (e) {
                        console.warn(e)
                    })
            } catch (e) {
                console.warn(e)
            }
        }

        const initEvents = () => {
            // check script loader data
            if (!window.geScriptLoaderData) {
                return
            }

            // ---------------------------GENERAL EVENTS--------------------------- //
            // 'window.GlobalE.SetMerchantParameters' event
            window.GlobalE.SetMerchantParameters(
                JSON.parse(window.geScriptLoaderData.clientSettings)
            )

            // 'window.GlobalE.ScriptsURL' event
            window.GlobalE.ScriptsURL(window.geScriptLoaderData.clientJsDomain)

            // 'window.GlobalE.SetCookieDomain' event
            window.GlobalE.SetCookieDomain(window.geScriptLoaderData.cookieDomain)

            // 'window.GlobalE.OnShippingSwitcherClosed' event
            window.GlobalE.OnShippingSwitcherClosed(onShippingSwitcherClosedHandler)
        }

        const initCheckoutEvents = () => {
            // ---------------------------CHECKOUT EVENTS--------------------------- //
            var geIframe = document.querySelector('#gle_iframe')
            if (geIframe) {
                // 'GlobalE.Checkout' event
                window.GlobalE.Checkout(geIframe.dataset.cartToken, 'gle_iframe')

                // 'KeepAlive' feature
                initKeepAlive()

                // 'GlobalE.OnCheckoutStepLoaded' event
                window.GlobalE.OnCheckoutStepLoaded(onCheckoutStepLoadedHandler)

                // 'Customer registration' feature
                window.addEventListener('message', registrationPostMessageHandler, false)
                return () =>
                    window.removeEventListener('message', registrationPostMessageHandler, false)
            }
        }

        const initAll = async () => {
            let geIframe = document.querySelector('#gle_iframe')

            if (geIframe || window?.location?.pathname === '/checkout') {
                if (cookie.get('reinitCheckout') === 'true') {
                    try {
                        if (!window.geScriptLoaderData) {
                            initScriptLoaderData()
                            await initClientSDK()
                            initEvents()
                        }
                        initCheckoutEvents()
                    } catch (err) {
                        console.error(err)
                    }

                    cookie.set('reinitCheckout', 'false')
                }
            } else {
                initScriptLoaderData()
                try {
                    await initClientSDK()
                    initEvents()
                } catch (err) {
                    console.error(err)
                }
            }
        }

        initAll()
    }, [geClientJsMerchantId, site?.id, cookie, currencyCodeContext, countryISOContext])

    return <></>
}

export default GlobalEScript
